/**
 * AWAC funnel analytics.
 *
 * Lightweight, dependency-free event tracker for the Lib Mont-Saint-Hilaire
 * pilot funnel. Every tracked event is fanned out to three sinks:
 *
 *   1. window.dataLayer (GTM-compatible push)
 *   2. a DOM CustomEvent `awac:analytics` (for any in-page listener)
 *   3. a sessionStorage ring buffer (for debugging / session replay)
 *
 * Funnel event names (use these constants, do not inline strings):
 *   chat_opened          – messaging window opened (source: launcher/cta/proactive)
 *   envelope_rendered    – an AWAC envelope was rendered (template name in detail)
 *   gallery_action       – user clicked an action inside a gallery template
 *   slot_picked          – user picked a visit slot (or first-available)
 *   booking_confirmed    – booking confirmation rendered
 *   confirm_synthesized  – client synthesized the BookingConfirm envelope
 *                          from a paraphrased agent confirmation
 *   proactive_shown      – proactive nudge displayed
 *   proactive_engaged    – proactive nudge clicked
 *   calendar_added       – user downloaded .ics or opened Google Calendar link
 */

const BUFFER_KEY = 'AWAC_ANALYTICS_BUFFER';
const BUFFER_MAX = 50;

export const AwacEvents = {
    CHAT_OPENED: 'chat_opened',
    ENVELOPE_RENDERED: 'envelope_rendered',
    GALLERY_ACTION: 'gallery_action',
    SLOT_PICKED: 'slot_picked',
    BOOKING_CONFIRMED: 'booking_confirmed',
    CONFIRM_SYNTHESIZED: 'confirm_synthesized',
    PROACTIVE_SHOWN: 'proactive_shown',
    PROACTIVE_ENGAGED: 'proactive_engaged',
    CALENDAR_ADDED: 'calendar_added',
    MAP_COMMAND_SENT: 'map_command_sent',
    MAP_COMMAND_RESULT: 'map_command_result'
};

function readBuffer() {
    try {
        const raw = sessionStorage.getItem(BUFFER_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
        return [];
    }
}

function writeBuffer(buffer) {
    try {
        sessionStorage.setItem(BUFFER_KEY, JSON.stringify(buffer.slice(-BUFFER_MAX)));
    } catch (e) {
        // Storage full or unavailable — analytics must never break the app.
    }
}

/**
 * Track a funnel event.
 * @param {string} event - one of AwacEvents.
 * @param {object} [detail] - flat serializable payload.
 */
export function track(event, detail = {}) {
    const record = {
        event: `awac_${event}`,
        ts: new Date().toISOString(),
        ...detail
    };

    // 1. GTM-style dataLayer push.
    try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(record);
    } catch (e) {
        // no-op
    }

    // 2. DOM CustomEvent for in-page listeners.
    try {
        window.dispatchEvent(new CustomEvent('awac:analytics', { detail: record }));
    } catch (e) {
        // no-op
    }

    // 3. Ring buffer in sessionStorage.
    const buffer = readBuffer();
    buffer.push(record);
    writeBuffer(buffer);
}

/**
 * Return the buffered events for this tab session (debug helper).
 */
export function getBufferedEvents() {
    return readBuffer();
}
